import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import Loader from '@rsos/components/capstone/Loader';
import { Box, Button, CheckBox, Text } from '@rsos/components/capstone/base';
import {
  GISServiceTypes,
  MapLayerSettings,
  selectGISServices,
  type GISService,
} from '@rsos/reduxDataStore';
import { notifyError, notifySuccess } from '@rsos/utils/toastUtils';
import { AdminDispatch } from '../../../../app/store';
import { AdminState } from '../../../../reducers';
import {
  mapLayerSettingsSavingSelector,
  mapLayerSettingsSelector,
  saveMapLayerSettings,
  setMapLayerSettings,
} from '../../state/MapLayerSettingsSlice';
import { SpatialLayer } from '../../state/spatialLayersSlice';
import styles from './MapLayers.module.css';
import useGISToken from './useGISToken';

const MIN_OPACITY = 0;
const MAX_OPACITY = 100;

type OpacityField = 'opacity' | 'companion_opacity' | 'secondary_cql_opacity';

const clampOpacity = (value: number) =>
  Math.max(MIN_OPACITY, Math.min(MAX_OPACITY, value));

function pickOpacityFields(
  existing: MapLayerSettings | undefined,
): Pick<
  MapLayerSettings,
  'opacity' | 'companion_opacity' | 'secondary_cql_opacity'
> {
  return {
    ...(existing?.opacity !== undefined ? { opacity: existing.opacity } : {}),
    ...(existing?.companion_opacity !== undefined
      ? { companion_opacity: existing.companion_opacity }
      : {}),
    ...(existing?.secondary_cql_opacity !== undefined
      ? { secondary_cql_opacity: existing.secondary_cql_opacity }
      : {}),
  };
}

function companionParts(layer: GISService): {
  hasCompanion: boolean;
  secondaryTitle: string | null;
} {
  const companion = layer.attributes?.raster_companion;
  const hasCompanion = Boolean(
    layer.service_type === GISServiceTypes.wfs && companion?.enabled,
  );
  const secondaryFilter = companion?.secondary_cql?.cql_filter?.trim();
  const secondaryTitle = secondaryFilter
    ? companion?.secondary_cql?.title?.trim() || 'Secondary'
    : null;
  return { hasCompanion, secondaryTitle };
}

export function MapLayers() {
  useGISToken();
  const dispatch: AdminDispatch = useDispatch();

  const gisServices = useSelector(selectGISServices);

  const settings = useSelector(mapLayerSettingsSelector);
  const saving = useSelector(mapLayerSettingsSavingSelector);
  const loading = useSelector<AdminState, boolean>(
    state =>
      !state.spatialLayers.initialized ||
      state.gisServices.loading ||
      !state.mapLayerSettings.initialized,
  );
  const spatialLayers = useSelector<AdminState, SpatialLayer[]>(
    state => state.spatialLayers.layers,
  );

  const [filter, setFilter] = useState('');
  const normalizedFilter = filter.trim().toLowerCase();

  const filteredGISServices = useMemo(
    () =>
      normalizedFilter
        ? gisServices.filter(layer =>
            (layer.display_name ?? '').toLowerCase().includes(normalizedFilter),
          )
        : gisServices,
    [gisServices, normalizedFilter],
  );

  const filteredSpatialLayers = useMemo(
    () =>
      normalizedFilter
        ? spatialLayers.filter(layer =>
            `${layer.title ?? ''} ${layer.name ?? ''}`
              .toLowerCase()
              .includes(normalizedFilter),
          )
        : spatialLayers,
    [spatialLayers, normalizedFilter],
  );

  const totalCount = gisServices.length + spatialLayers.length;
  const filteredCount =
    filteredGISServices.length + filteredSpatialLayers.length;

  const updateChecked = useCallback(
    (id: number, default_checked: boolean) => {
      const existing = settings[id];
      dispatch(
        setMapLayerSettings({
          ...settings,
          [id]: {
            id,
            default_checked,
            ...pickOpacityFields(existing),
          },
        }),
      );
    },
    [settings, dispatch],
  );

  const updateOpacityField = useCallback(
    (id: number, field: OpacityField, value: number | undefined) => {
      const existing = settings[id];
      const next = {
        id,
        default_checked: existing?.default_checked ?? false,
        ...pickOpacityFields(existing),
      };
      if (value === undefined) {
        delete next[field];
      } else {
        next[field] = value;
      }
      dispatch(
        setMapLayerSettings({
          ...settings,
          [id]: next,
        }),
      );
    },
    [settings, dispatch],
  );

  const handleOpacityChange =
    (id: number, field: OpacityField) => (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.currentTarget.value;
      if (raw === '') {
        updateOpacityField(id, field, undefined);
        return;
      }
      const parsed = Number(raw);
      if (Number.isNaN(parsed)) {
        return;
      }
      updateOpacityField(id, field, clampOpacity(parsed));
    };

  const save = useCallback(() => {
    dispatch(saveMapLayerSettings(settings))
      .unwrap()
      .then(() => notifySuccess('Map layer defaults have been saved.'))
      .catch(() => notifyError('Map layer defaults failed to save.'));
  }, [settings, dispatch]);

  const renderOpacityInput = (
    id: number,
    field: OpacityField,
    ariaLabel: string,
  ) => (
    <span className={styles.opacityControl}>
      <input
        type="number"
        min={MIN_OPACITY}
        max={MAX_OPACITY}
        step={1}
        value={settings[id]?.[field] ?? ''}
        onChange={handleOpacityChange(id, field)}
        placeholder="40"
        aria-label={ariaLabel}
        className={styles.opacityInput}
      />
      <span className={styles.opacitySuffix}>%</span>
    </span>
  );

  const renderOpacityCell = (id: number, isDisabledRow: boolean) => {
    if (isDisabledRow) {
      return <span className={styles.disabledOpacity}>—</span>;
    }
    return renderOpacityInput(id, 'opacity', 'Default layer opacity (0-100)');
  };

  const renderCompanionOpacityCell = (
    layer: GISService,
    secondaryTitle: string | null,
  ) => (
    <div className={styles.companionOpacityStack}>
      <div className={styles.companionOpacityRow}>
        <span className={styles.companionOpacityLabel}>Footprint</span>
        {renderOpacityInput(
          layer.id,
          'opacity',
          `${layer.display_name} Footprint opacity (0-100)`,
        )}
      </div>
      <div className={styles.companionOpacityRow}>
        <span className={styles.companionOpacityLabel}>Image</span>
        {renderOpacityInput(
          layer.id,
          'companion_opacity',
          `${layer.display_name} Image opacity (0-100)`,
        )}
      </div>
      {secondaryTitle ? (
        <div className={styles.companionOpacityRow}>
          <span className={styles.companionOpacityLabel}>{secondaryTitle}</span>
          {renderOpacityInput(
            layer.id,
            'secondary_cql_opacity',
            `${layer.display_name} ${secondaryTitle} opacity (0-100)`,
          )}
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      <Box flow={10} mb="20px">
        <Text size="header" fontWeight="semibold" color="primaryText" mb="10px">
          Map Layers
        </Text>
        <Text size="large" color="primaryText">
          Set which layers will be on by default when users log in, and the
          default opacity for each layer (0–100). For WFS layers with a raster
          companion, set opacity separately for Footprint, Image, and Secondary.
        </Text>{' '}
        <Text size="small" color="primaryText">
          <strong>Note:</strong> These controls do not override roles and
          permissions! If a user does not have permission to see a layer, they
          will not see that layer even if it is set to be on by default.
        </Text>
      </Box>

      <div className={styles.card}>
        {loading && (
          <div className={styles.loadingBox}>
            <Loader text="Fetching.." />
          </div>
        )}
        {!loading && (
          <>
            <div className={styles.toolbar}>
              <div className={styles.filterWrapper}>
                <input
                  type="search"
                  value={filter}
                  onChange={e => setFilter(e.currentTarget.value)}
                  placeholder="Filter layers…"
                  aria-label="Filter layers"
                  className={styles.filterInput}
                />
                {filter && (
                  <button
                    type="button"
                    className={styles.clearFilter}
                    onClick={() => setFilter('')}
                    aria-label="Clear filter"
                  >
                    ×
                  </button>
                )}
              </div>
              <span className={styles.count}>
                {normalizedFilter
                  ? `${filteredCount} of ${totalCount} layers`
                  : `${totalCount} layers`}
              </span>
            </div>

            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Layer</th>
                    <th className={styles.checkboxCell}>Default On</th>
                    <th className={styles.opacityCell}>Opacity</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGISServices.length > 0 && (
                    <>
                      <tr className={styles.sectionRow}>
                        <td colSpan={3}>
                          GIS Services ({filteredGISServices.length})
                        </td>
                      </tr>
                      {filteredGISServices.map(layer => {
                        const isBasemap =
                          layer.service_type == GISServiceTypes.basemap;
                        const isGeocoder =
                          layer.service_type == GISServiceTypes.geocode;
                        const isRowDisabled = isBasemap || isGeocoder;
                        const { hasCompanion, secondaryTitle } =
                          companionParts(layer);
                        return (
                          <tr key={`gis-${layer.id}`}>
                            <td>
                              <span className={styles.layerName}>
                                <span className={styles.layerNamePrimary}>
                                  {layer.display_name}
                                </span>
                                {(isBasemap || isGeocoder) && (
                                  <span className={styles.layerNameSecondary}>
                                    {isBasemap ? 'Basemap' : 'Geocoder'} ·
                                    always on
                                  </span>
                                )}
                                {hasCompanion && (
                                  <span className={styles.layerNameSecondary}>
                                    WFS · Footprint
                                    {secondaryTitle
                                      ? ` / Image / ${secondaryTitle}`
                                      : ' / Image'}
                                  </span>
                                )}
                              </span>
                            </td>
                            <td className={styles.checkboxCell}>
                              <CheckBox
                                isChecked={
                                  !!settings[layer.id]?.default_checked ||
                                  isBasemap ||
                                  isGeocoder
                                }
                                disabled={isRowDisabled}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                  updateChecked(
                                    layer.id,
                                    e.currentTarget.checked,
                                  );
                                }}
                              />
                            </td>
                            <td className={styles.opacityCell}>
                              {isRowDisabled
                                ? renderOpacityCell(layer.id, true)
                                : hasCompanion
                                  ? renderCompanionOpacityCell(
                                      layer,
                                      secondaryTitle,
                                    )
                                  : renderOpacityCell(layer.id, false)}
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  )}
                  {filteredSpatialLayers.length > 0 && (
                    <>
                      <tr className={styles.sectionRow}>
                        <td colSpan={3}>
                          Spatial Layers ({filteredSpatialLayers.length})
                        </td>
                      </tr>
                      {filteredSpatialLayers.map(layer => (
                        <tr key={`spatial-${layer.id}`}>
                          <td>
                            <span className={styles.layerName}>
                              <span className={styles.layerNamePrimary}>
                                {layer.title}
                              </span>
                              <span className={styles.layerNameSecondary}>
                                {layer.name}
                                {layer.auto_display ? ' · always on' : ''}
                              </span>
                            </span>
                          </td>
                          <td className={styles.checkboxCell}>
                            <CheckBox
                              disabled={layer.auto_display}
                              isChecked={
                                !!settings[layer.id]?.default_checked ||
                                layer.auto_display
                              }
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                updateChecked(
                                  layer.id,
                                  e.currentTarget.checked,
                                );
                              }}
                            />
                          </td>
                          <td className={styles.opacityCell}>
                            {renderOpacityCell(layer.id, layer.auto_display)}
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                  {filteredCount === 0 && (
                    <tr>
                      <td colSpan={3} className={styles.emptyState}>
                        No layers match “{filter}”
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className={styles.footer}>
              <Button btnType="success" isLoadingProp={saving} onClick={save}>
                Save
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
